import {
  Alert,
  Anchor,
  Button,
  Center,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ArtistLink } from '@wwaave/db/schema';
import { useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { MobileVerifyField } from '../../components/MobileVerifyField';
import { PhoneNumberInput } from '../../components/PhoneNumberInput';
import { ArtistGalleryField, ArtistPhotoField } from '../../components/profile/ArtistImageFields';
import { ArtistLinksField } from '../../components/profile/ArtistLinksField';
import { EmptyState } from '../../components/ui';
import { ChoiceCard } from '../../components/wizard/ChoiceCard';
import { JourneyStrip } from '../../components/wizard/JourneyStrip';
import { type ChecklistItem, NextStepsChecklist } from '../../components/wizard/NextStepsChecklist';
import { countryOptions, groupedArtFormOptions } from '../../components/wizard/vocab-options';
import { Wizard, type WizardStep } from '../../components/wizard/Wizard';
import { ApiError, fetchOk, mutateJson } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

/**
 * /onboarding/artist — artist profile setup as a one-question-per-screen
 * wizard.
 *
 * Every step saves as you leave it (PATCH per step, images upload on pick), so
 * closing the tab loses nothing and coming back resumes at the first step still
 * missing. The steps run in the order that matters to a host reading a request:
 * face, place, craft, story, then the extras that earn the Verified badge
 * (a link + a verified mobile, alongside the photo — services/artist-auto-verify)
 * and the identity details a host needs before confirming a stay
 * (isProfileComplete). The last screen says plainly what's done and what's left.
 */

type ArtistProfile = {
  slug: string;
  bio: string | null;
  city: string | null;
  country: string | null;
  discipline_summary: string | null;
  profile_photo_url: string | null;
  published: boolean;
  links?: ArtistLink[];
  gallery_image_urls?: string[];
  verified_at?: number | string | null;
};

type ProfileResponse = { ok: true; profile: ArtistProfile; art_forms: string[] };

type AccountResponse = {
  ok: boolean;
  account: {
    displayName: string;
    legalFullName: string | null;
    mobileNumber: string | null;
    mobileVerifiedAt: string | null;
    emergencyContactName: string | null;
    emergencyContactPhone: string | null;
  };
};

type Values = {
  city: string;
  country: string;
  art_forms: string[];
  discipline_summary: string;
  bio: string;
  links: ArtistLink[];
  legalFullName: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  published: boolean;
};

const HTTPS_URL_RE = /^https:\/\/[^\s]+$/i;
const PHONE_RE = /^\+[1-9]\d{6,14}$/;

const PROFILE_KEY = ['profile', 'me', 'artist'] as const;
const ACCOUNT_KEY = ['users', 'me', 'account'] as const;

async function fetchProfile(): Promise<ProfileResponse | null> {
  try {
    const res = await fetchOk('/api/profiles/me/artist', { credentials: 'include' });
    return (await res.json()) as ProfileResponse;
  } catch (err) {
    // 403 (no artist capability) / 404 (no profile row yet) = not set up.
    if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
    throw err;
  }
}

function ArtistOnboarding() {
  const { capabilities, isLoading: authLoading } = useAuth();
  const profileQ = useQuery({
    queryKey: PROFILE_KEY,
    queryFn: fetchProfile,
    enabled: !authLoading,
  });
  const accountQ = useQuery({
    queryKey: ACCOUNT_KEY,
    queryFn: async () => (await (await fetchOk('/api/users/me/account', { credentials: 'include' })).json()) as AccountResponse,
  });

  if (authLoading || profileQ.isPending || accountQ.isPending) {
    return (
      <AppShell tone="form">
        <Center mih={280}>
          <Loader />
        </Center>
      </AppShell>
    );
  }
  if (profileQ.isError || accountQ.isError) {
    return (
      <AppShell tone="form">
        <EmptyState
          title="We couldn't load your profile."
          body="Refresh the page to try again."
        />
      </AppShell>
    );
  }
  // A host adding the artist side has no artist profile yet. Creating one is a
  // real change to their account, so it's an explicit first click, not a
  // side effect of opening the page.
  if (profileQ.data === null || capabilities?.isArtist !== true) {
    return <AddArtistSide />;
  }
  return <ArtistWizard profile={profileQ.data} account={accountQ.data} />;
}

function AddArtistSide() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const add = useMutation({
    mutationFn: async () => {
      try {
        await mutateJson('/api/auth/add-capability', { kind: 'artist' });
      } catch (err) {
        // 409 = the artist side already exists; carry on.
        if (!(err instanceof ApiError && err.status === 409)) throw err;
      }
      await refresh();
      await qc.invalidateQueries({ queryKey: PROFILE_KEY });
    },
  });
  return (
    <AppShell tone="form">
      <Stack gap="lg" py="md">
        <JourneyStrip journey="artist" current="profile" skip={['account']} />
        <Stack gap="xs">
          <Text size="xs" tt="uppercase" fw={700} c="brand.7" style={{ letterSpacing: '0.1em' }}>
            Artist profile
          </Text>
          <Text fw={700} style={{ fontSize: 'clamp(1.45rem, 3.2vw, 2rem)', lineHeight: 1.18 }}>
            Add an artist side to your account.
          </Text>
          <Text c="dimmed">
            Same sign-in, a second hat. You&apos;ll switch between hosting and touring from the
            sidebar. It takes about five minutes, and it&apos;s always free for artists.
          </Text>
        </Stack>
        {add.isError ? (
          <Alert color="red" variant="light">
            {add.error instanceof Error ? add.error.message : "We couldn't set that up. Try again."}
          </Alert>
        ) : null}
        <Group>
          <Button
            size="md"
            variant="gradient"
            gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            Start my artist profile
          </Button>
        </Group>
      </Stack>
    </AppShell>
  );
}

function ArtistWizard({ profile: p, account: a }: { profile: ProfileResponse; account: AccountResponse }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const displayName = a.account.displayName || user?.displayName || 'you';

  const [photoUrl, setPhotoUrl] = useState<string | null>(p.profile.profile_photo_url);
  const [gallery, setGallery] = useState<string[]>(p.profile.gallery_image_urls ?? []);
  const [mobileVerified, setMobileVerified] = useState(a.account.mobileVerifiedAt !== null);
  // What's actually on the server, as opposed to what's typed: a skipped
  // optional step saves nothing, and the closing checklist must not claim it.
  const [saved, setSaved] = useState(() => ({
    city: (p.profile.city ?? '') !== '',
    links: (p.profile.links ?? []).length > 0,
    safety:
      (a.account.legalFullName ?? '') !== '' &&
      (a.account.emergencyContactName ?? '') !== '' &&
      (a.account.emergencyContactPhone ?? '') !== '',
    published: p.profile.published,
  }));

  const form = useForm<Values>({
    initialValues: {
      city: p.profile.city ?? '',
      country: p.profile.country ?? 'AU',
      art_forms: p.art_forms,
      discipline_summary: p.profile.discipline_summary ?? '',
      bio: p.profile.bio ?? '',
      links: p.profile.links ?? [],
      legalFullName: a.account.legalFullName ?? '',
      emergencyContactName: a.account.emergencyContactName ?? '',
      emergencyContactPhone: a.account.emergencyContactPhone ?? '',
      // A brand-new profile defaults to "yes, let hosts find me" on the last
      // step; an existing choice is respected.
      published: p.profile.published || p.profile.bio === null,
    },
    validate: {
      city: (v) => (v.trim().length === 0 ? 'Tell hosts where to find you.' : null),
      art_forms: (v) => (v.length === 0 ? 'Pick at least one.' : null),
      bio: (v) =>
        v.trim().length === 0
          ? 'A few sentences is plenty.'
          : v.length > 2000
            ? 'Keep it under 2,000 characters.'
            : null,
      links: (v) => {
        for (const link of v) {
          if (link.url.trim().length === 0) return 'Every link needs an address, or remove it.';
          if (!HTTPS_URL_RE.test(link.url)) return 'Links start with https://';
        }
        return null;
      },
      emergencyContactPhone: (v) =>
        v.trim() !== '' && !PHONE_RE.test(v.trim()) ? 'That number looks incomplete.' : null,
    },
  });
  const v = form.values;

  const hasPhoto = photoUrl !== null && photoUrl.length > 0;
  const hasLink = saved.links;
  const safetyDone = saved.safety;

  function valid(fields: (keyof Values)[]): boolean {
    let ok = true;
    for (const f of fields) if (form.validateField(f).hasError) ok = false;
    return ok;
  }

  async function saveProfile(body: Record<string, unknown>) {
    await mutateJson('/api/profiles/me/artist', body, { method: 'PATCH' });
  }

  // Keep the rest of the app (profile page, dashboard nudges) in step once the
  // visitor leaves the wizard.
  useEffect(
    () => () => {
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY });
    },
    [qc],
  );

  const steps: WizardStep[] = [
    {
      id: 'photo',
      label: 'Photo',
      title: 'First, show hosts who’s coming.',
      lede: 'A clear photo of you or your act. One good one beats five blurry ones.',
      onNext: () => {
        if (!hasPhoto) throw new Error('Add a photo so hosts can see who they’re hearing from.');
      },
      body: <ArtistPhotoField focusFirst name={displayName} value={photoUrl} onChange={setPhotoUrl} />,
    },
    {
      id: 'home',
      label: 'Home base',
      title: 'Where do you call home?',
      lede: 'Hosts like to know how far you’re travelling. Your city is enough.',
      onNext: async () => {
        if (!valid(['city'])) return false;
        await saveProfile({ city: v.city.trim(), ...(v.country.length === 2 ? { country: v.country } : {}) });
        setSaved((s) => ({ ...s, city: true }));
      },
      body: (
        <Group grow align="flex-start" wrap="wrap">
          <TextInput
            data-autofocus
            size="md"
            label="City or town"
            placeholder="e.g. Brisbane, Byron Bay, Noosa"
            style={{ minWidth: 200 }}
            {...form.getInputProps('city')}
          />
          <Select
            size="md"
            label="Country"
            data={countryOptions()}
            searchable
            allowDeselect={false}
            style={{ minWidth: 200 }}
            {...form.getInputProps('country')}
          />
        </Group>
      ),
    },
    {
      id: 'craft',
      label: 'What you do',
      title: 'What do you do?',
      lede: 'Pick everything that fits. Hosts filter by these.',
      onNext: async () => {
        if (!valid(['art_forms'])) return false;
        await saveProfile({ art_forms: v.art_forms, discipline_summary: v.discipline_summary.trim() });
      },
      body: (
        <Stack gap="md">
          <MultiSelect
            data-autofocus
            size="md"
            label="Art forms"
            placeholder={v.art_forms.length === 0 ? 'Start typing, e.g. singer' : undefined}
            data={groupedArtFormOptions()}
            searchable
            hidePickedOptions
            maxDropdownHeight={280}
            {...form.getInputProps('art_forms')}
          />
          <TextInput
            size="md"
            label="Sum it up in a line"
            description="Optional. The first thing a host reads."
            placeholder="Acoustic duo, two voices and a guitar"
            maxLength={160}
            {...form.getInputProps('discipline_summary')}
          />
        </Stack>
      ),
    },
    {
      id: 'bio',
      label: 'Your story',
      title: 'What would you bring to a room?',
      lede: 'Two or three sentences: what you play, who it’s for, what a night with you is like.',
      onNext: async () => {
        if (!valid(['bio'])) return false;
        await saveProfile({ bio: v.bio.trim() });
      },
      body: (
        <Stack gap={4}>
          <Textarea
            data-autofocus
            size="md"
            aria-label="Bio"
            minRows={5}
            maxRows={14}
            autosize
            {...form.getInputProps('bio')}
          />
          <Text size="xs" c="dimmed" ta="right">
            {v.bio.length} / 2,000
          </Text>
        </Stack>
      ),
    },
    {
      id: 'gallery',
      label: 'Gallery',
      title: 'Show them the show.',
      lede: 'Live shots, press photos, your studio. Up to 12. They save as soon as they upload.',
      optional: true,
      body: <ArtistGalleryField focusFirst value={gallery} onChange={setGallery} />,
    },
    {
      id: 'links',
      label: 'Links',
      title: 'Where can hosts hear you?',
      lede: 'Spotify, Bandcamp, Instagram, your own site. One link, plus your photo and a verified mobile, earns your Verified badge.',
      optional: true,
      onNext: async () => {
        if (!valid(['links'])) return false;
        await saveProfile({ links: v.links });
        setSaved((s) => ({ ...s, links: v.links.length > 0 }));
      },
      body: (
        <ArtistLinksField
          value={v.links}
          onChange={(next) => form.setFieldValue('links', next)}
          error={form.errors['links']}
        />
      ),
    },
    {
      id: 'mobile',
      label: 'Mobile',
      title: 'Verify your mobile.',
      lede: 'We text you a code. Hosts need it verified before they can confirm a stay, and it completes your Verified badge.',
      optional: !mobileVerified,
      body: (
        <MobileVerifyField
          initialNumber={a.account.mobileNumber ?? ''}
          verified={mobileVerified}
          onVerified={() => setMobileVerified(true)}
        />
      ),
      onNext: () => {
        if (!mobileVerified) throw new Error('Verify your number first, or skip this for now.');
      },
    },
    {
      id: 'safety',
      label: 'Safety details',
      title: 'Who should we call if something goes wrong?',
      lede: 'Your legal name and an emergency contact. Shared with your host only during a confirmed stay, and needed before a host can confirm.',
      optional: true,
      onNext: async () => {
        if (!valid(['emergencyContactPhone'])) return false;
        const body: Record<string, string> = {};
        if (v.legalFullName.trim() !== '') body.legalFullName = v.legalFullName.trim();
        if (v.emergencyContactName.trim() !== '') body.emergencyContactName = v.emergencyContactName.trim();
        if (v.emergencyContactPhone.trim() !== '') body.emergencyContactPhone = v.emergencyContactPhone.trim();
        if (Object.keys(body).length > 0) await mutateJson('/api/users/me/account', body, { method: 'PATCH' });
        setSaved((s) => ({
          ...s,
          safety:
            v.legalFullName.trim() !== '' &&
            v.emergencyContactName.trim() !== '' &&
            v.emergencyContactPhone.trim() !== '',
        }));
      },
      body: (
        <Stack gap="md">
          <TextInput
            data-autofocus
            size="md"
            label="Your legal full name"
            placeholder="As it appears on your ID"
            autoComplete="name"
            {...form.getInputProps('legalFullName')}
          />
          <TextInput
            size="md"
            label="Emergency contact’s name"
            {...form.getInputProps('emergencyContactName')}
          />
          <PhoneNumberInput
            label="Emergency contact’s phone"
            value={v.emergencyContactPhone}
            onChange={(next) => form.setFieldValue('emergencyContactPhone', next)}
            error={form.errors['emergencyContactPhone']}
          />
        </Stack>
      ),
    },
    {
      id: 'publish',
      label: 'Go live',
      title: 'Ready to be found?',
      lede: 'A published profile shows up when hosts browse artists. You can change this any time from your profile.',
      nextLabel: 'Finish',
      onNext: async () => {
        await saveProfile({ published: v.published });
        setSaved((s) => ({ ...s, published: v.published }));
      },
      body: (
        <Stack gap="sm" role="radiogroup" aria-label="Profile visibility">
          <ChoiceCard
            focusFirst
            selected={v.published}
            onSelect={() => form.setFieldValue('published', true)}
            title="Yes, publish my profile"
            description="Hosts can find you and you can send requests straight away."
          />
          <ChoiceCard
            selected={!v.published}
            onSelect={() => form.setFieldValue('published', false)}
            title="Not yet"
            description="Keep it private for now. You can still send requests; hosts see your profile when you do."
          />
        </Stack>
      ),
    },
    {
      id: 'done',
      label: 'Done',
      title: `You’re set up, ${displayName}.`,
      lede: 'Here’s where things stand. Anything still to do can wait until your first request.',
      hideNext: true,
      // Each unfinished item carries its own way back.
      lockBack: true,
      body: (
        <Stack gap="lg">
          <NextStepsChecklist items={doneChecklist()} />
          <Text size="sm" c="dimmed">
            Before you head out to a stay, read our{' '}
            <Anchor component={Link} to="/community-guidelines" fw={600}>
              safety guidance
            </Anchor>
            . In an emergency, always call 000 first.
          </Text>
          <Group gap="sm">
            <Button
              component={Link}
              to="/browse"
              size="md"
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              Find venues
            </Button>
            <Button component={Link} to="/profile" size="md" variant="light">
              See my profile
            </Button>
            <Button component={Link} to="/dashboard" size="md" variant="subtle">
              Go to my dashboard
            </Button>
          </Group>
        </Stack>
      ),
    },
  ];

  function doneChecklist(): ChecklistItem[] {
    const verified = hasPhoto && hasLink && mobileVerified;
    return [
      {
        id: 'profile',
        label: 'Profile',
        detail: 'Photo, home base, what you do and your story.',
        done: hasPhoto && saved.city && v.art_forms.length > 0 && v.bio.trim() !== '',
      },
      {
        id: 'gallery',
        label: 'Gallery',
        detail: 'Optional, but hosts love seeing a show.',
        done: gallery.length > 0,
        action: <StepJump label="Add images" onJump={() => jumpTo('gallery')} />,
      },
      {
        id: 'verified',
        label: 'Verified badge',
        detail: verified
          ? 'Hosts see the badge on your profile and requests.'
          : `Needs a photo, a link and a verified mobile. Still missing: ${[
              !hasPhoto && 'photo',
              !hasLink && 'a link',
              !mobileVerified && 'verified mobile',
            ]
              .filter(Boolean)
              .join(', ')}.`,
        done: verified,
        action: <StepJump label="Finish it" onJump={() => jumpTo(!hasLink ? 'links' : 'mobile')} />,
      },
      {
        id: 'safety',
        label: 'Ready for a host to confirm',
        detail: 'A verified mobile, your legal name and an emergency contact.',
        done: mobileVerified && safetyDone,
        action: <StepJump label="Add them" onJump={() => jumpTo(!mobileVerified ? 'mobile' : 'safety')} />,
      },
      {
        id: 'published',
        label: 'Profile published',
        detail: saved.published
          ? 'Hosts can find you.'
          : 'Private for now. Publish from your profile when you’re ready.',
        done: saved.published,
      },
    ];
  }

  const stepIds = steps.map((s) => s.id);
  const [step, setStep] = useState(() => firstIncompleteStep(p, hasPhoto));

  function jumpTo(id: string) {
    const i = stepIds.indexOf(id);
    if (i >= 0) setStep(i);
  }

  const completeById: Record<string, boolean> = {
    photo: hasPhoto,
    home: saved.city,
    craft: v.art_forms.length > 0,
    bio: v.bio.trim() !== '',
    gallery: gallery.length > 0,
    links: hasLink,
    mobile: mobileVerified,
    safety: safetyDone,
    publish: saved.published,
  };

  const isDone = step === stepIds.indexOf('done');
  const journeyStage = isDone ? (hasPhoto && hasLink && mobileVerified ? 'ready' : 'verified') : 'profile';

  return (
    <AppShell tone="form">
      <Stack gap="lg" py="md">
        <JourneyStrip journey="artist" current={journeyStage} />
        <Wizard
          eyebrow="Your artist profile"
          steps={steps}
          index={step}
          onIndexChange={setStep}
          isStepDone={(s, i) => i < step && completeById[s.id] === true}
        />
      </Stack>
    </AppShell>
  );
}

// Resume where it matters: the first required step still empty, else the start
// (someone returning to polish things wants the whole walk-through). Indexes
// follow the step order in ArtistWizard.
function firstIncompleteStep(p: ProfileResponse, hasPhoto: boolean): number {
  if (!hasPhoto) return 0;
  if ((p.profile.city ?? '') === '') return 1;
  if (p.art_forms.length === 0) return 2;
  if ((p.profile.bio ?? '') === '') return 3;
  return 0;
}

function StepJump({ label, onJump }: { label: string; onJump: () => void }) {
  return (
    <Button size="compact-sm" variant="light" onClick={onJump}>
      {label}
    </Button>
  );
}

export const Route = createFileRoute('/onboarding/artist')({
  component: ArtistOnboarding,
});
